// src/capabilities/CapabilitiesModal.js
// Alta / edición / vista de una competencia.
// Desde PLAN_CASOS_ESTUDIO.md (2026-09-02) la competencia ya no redacta sus casos:
// vincula casos del catálogo (caseStudies) y define las preguntas para cada uno
// (capabilities.casos[] = [{ caseStudyId, preguntas[] }]). Los textos[] antiguos
// no se muestran ni se envían: se conservan intactos en Firestore para los
// assessments existentes (ver PLAN_CASOS_ESTUDIO.md §8).
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';

const emptyCaso = () => ({ caseStudyId: '', preguntas: [{ pregunta: '' }] });

const CapabilitiesModal = ({
  open,
  onClose,
  mode, // 'create', 'edit', 'view'
  selectedCapability,
  onSave,
  user,
  authToken,
  showAlert,
  language
}) => {
  const [formData, setFormData] = useState({ name: '', description: '', casos: [] });
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gptLoading, setGptLoading] = useState(false);
  const [questionsToGenerate, setQuestionsToGenerate] = useState(3);

  const { t } = useTranslation();
  const { project, organization } = useStore.getState();
  const headers = { Authorization: `Bearer ${authToken}` };

  const catalogById = useMemo(() => {
    const map = {};
    catalog.forEach((cs) => { map[cs.id || cs.uid] = cs; });
    return map;
  }, [catalog]);

  // ---------- API ----------
  const loadCatalog = async () => {
    try {
      setCatalogLoading(true);
      const response = await api.get('/case-studies', {
        params: { language, organizationId: organization, includeInactive: true },
        headers,
      });
      if (response.data.response && response.data.response.status_response === 'OK') {
        setCatalog(response.data.response.data || []);
      } else {
        showAlert(t('caseStudies.loadError'), 'warning');
      }
    } catch (err) {
      console.error('Error loading case studies catalog:', err);
      showAlert(t('caseStudies.loadError'), 'error');
    } finally {
      setCatalogLoading(false);
    }
  };

  const createCapability = async (capabilityData) => {
    const response = await api.post('/capabilities', capabilityData, { headers });
    return response.data;
  };

  const updateCapability = async (id, capabilityData) => {
    const response = await api.put(`/capabilities/${id}`, capabilityData, { headers });
    return response.data;
  };

  // Sugerir preguntas con IA a partir del contenido del caso vinculado
  const generateQuestionsWithGPT = async (casoIndex) => {
    const caso = formData.casos[casoIndex];
    const caseStudy = catalogById[caso.caseStudyId];
    if (!caseStudy) return;

    try {
      setGptLoading(true);
      const existingQuestions = caso.preguntas.map((p) => p.pregunta).filter((q) => q && q.trim() !== '');

      const requestData = {
        competencyName: formData.name,
        competencyDescription: formData.description || 'Competencia sin descripción específica',
        caseStudy: caseStudy.content,
        numberOfQuestions: questionsToGenerate,
        existingQuestions,
        language,
        projectId: project,
        organizationId: organization,
        referenceId: selectedCapability?.uid || null
      };

      const response = await api.post('/gpt-agent/generate-questions', requestData, { headers });

      if (response.data.response && response.data.response.status_response === 'OK') {
        const newQuestions = (response.data.response.data.questions || []).map((q) => ({ pregunta: q.pregunta || q }));

        setFormData((prev) => ({
          ...prev,
          casos: prev.casos.map((c, index) => {
            if (index !== casoIndex) return c;
            const current = [...c.preguntas];
            let k = 0;
            // Primero rellenar preguntas vacías, luego agregar al final
            for (let i = 0; i < current.length && k < newQuestions.length; i++) {
              if (!current[i].pregunta || current[i].pregunta.trim() === '') {
                current[i] = newQuestions[k++];
              }
            }
            while (k < newQuestions.length) current.push(newQuestions[k++]);
            return { ...c, preguntas: current };
          })
        }));

        showAlert(t('capabilities.generatedQuestions', { count: newQuestions.length }), 'success');
      } else {
        showAlert(t('capabilities.generatedQuestionsError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
      console.error('Error generating questions:', err);
    } finally {
      setGptLoading(false);
    }
  };

  const handleQuestionsCountChange = (value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) setQuestionsToGenerate(1);
    else if (numValue > 10) setQuestionsToGenerate(10);
    else setQuestionsToGenerate(numValue);
  };

  // ---------- Validación ----------
  const validateForm = () => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') errors.push(t('capability.nameError'));
    if (!formData.description || formData.description.trim() === '') errors.push(t('capability.descriptionError'));

    const seen = new Set();
    formData.casos.forEach((caso, index) => {
      const validQuestions = (caso.preguntas || []).filter((p) => (p.pregunta || '').trim() !== '');
      const hasData = caso.caseStudyId || validQuestions.length > 0;
      if (!hasData) return; // fila vacía: se descarta al guardar

      if (!caso.caseStudyId) {
        errors.push(t('capabilities.casoSelectError', { index: index + 1 }));
      } else if (seen.has(caso.caseStudyId)) {
        errors.push(t('capabilities.casoDuplicatedError', { index: index + 1 }));
      }
      seen.add(caso.caseStudyId);

      if (validQuestions.length === 0) {
        errors.push(t('capabilities.textQuestionError', { index: index + 1 }));
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // ---------- Inicialización ----------
  useEffect(() => {
    if (!open) return;
    setValidationErrors([]);
    setShowValidationErrors(false);
    setQuestionsToGenerate(3);
    loadCatalog();

    if (mode === 'create' || !selectedCapability) {
      setFormData({ name: '', description: '', casos: [] });
      return;
    }

    const casos = Array.isArray(selectedCapability.casos)
      ? selectedCapability.casos.map((caso) => ({
        caseStudyId: caso.caseStudyId || '',
        preguntas: caso.preguntas && caso.preguntas.length > 0
          ? caso.preguntas.map((p) => ({ id: p.id, pregunta: p.pregunta || '' }))
          : [{ pregunta: '' }],
      }))
      : [];

    setFormData({
      name: selectedCapability.name || '',
      description: selectedCapability.description || '',
      casos,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, selectedCapability]);

  const handleClose = () => {
    setValidationErrors([]);
    setShowValidationErrors(false);
    onClose();
  };

  const touch = () => { if (showValidationErrors) setShowValidationErrors(false); };

  const handleNameChange = (value) => { setFormData((prev) => ({ ...prev, name: value })); touch(); };
  const handleDescriptionChange = (value) => { setFormData((prev) => ({ ...prev, description: value })); touch(); };

  const handleCasoSelect = (casoIndex, caseStudyId) => {
    setFormData((prev) => ({
      ...prev,
      casos: prev.casos.map((c, i) => (i === casoIndex ? { ...c, caseStudyId } : c)),
    }));
    touch();
  };

  const handlePreguntaChange = (casoIndex, preguntaIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      casos: prev.casos.map((c, ci) => ci === casoIndex
        ? { ...c, preguntas: c.preguntas.map((p, pi) => (pi === preguntaIndex ? { ...p, pregunta: value } : p)) }
        : c),
    }));
    touch();
  };

  const addCaso = () => setFormData((prev) => ({ ...prev, casos: [...prev.casos, emptyCaso()] }));
  const removeCaso = (index) => setFormData((prev) => ({ ...prev, casos: prev.casos.filter((_, i) => i !== index) }));

  const addPregunta = (casoIndex) => {
    setFormData((prev) => ({
      ...prev,
      casos: prev.casos.map((c, i) => (i === casoIndex ? { ...c, preguntas: [...c.preguntas, { pregunta: '' }] } : c)),
    }));
  };

  const removePregunta = (casoIndex, preguntaIndex) => {
    const caso = formData.casos[casoIndex];
    if (caso.preguntas.length > 1) {
      setFormData((prev) => ({
        ...prev,
        casos: prev.casos.map((c, ci) => ci === casoIndex
          ? { ...c, preguntas: c.preguntas.filter((_, pi) => pi !== preguntaIndex) }
          : c),
      }));
    } else {
      showAlert(t('capabilities.atLeastOneQuestionError'), 'warning');
    }
  };

  // ---------- Guardar ----------
  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowValidationErrors(true);
      return;
    }

    try {
      setLoading(true);

      const cleanedCasos = (formData.casos || [])
        .map((caso) => ({
          caseStudyId: caso.caseStudyId,
          preguntas: (caso.preguntas || [])
            .map((p) => ({ ...(p.id ? { id: p.id } : {}), pregunta: (p.pregunta || '').trim() }))
            .filter((p) => p.pregunta !== ''),
        }))
        .filter((c) => c.caseStudyId && c.preguntas.length > 0);

      // NOTA: no se envía textos[] → el backend conserva los textos antiguos tal cual.
      const cleanData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        casos: cleanedCasos,
        lastUpdatedBy: user?.email || user?.uid || 'unknown',
        language,
        organizationId: organization
      };

      let response;
      if (mode === 'create') {
        response = await createCapability(cleanData);
      } else if (mode === 'edit' && selectedCapability) {
        response = await updateCapability(selectedCapability.id || selectedCapability.uid, cleanData);
      }

      if (response.response && response.response.status_response === 'OK') {
        handleClose();
        onSave();
        showAlert(t(mode === 'create' ? 'capabilities.capabilityCreated' : 'capabilities.capabilityUpdated'), 'success');
      } else {
        showAlert(response.response?.message || t('capabilities.capabilitySaveError'), 'error');
        setShowValidationErrors(false);
      }
    } catch (err) {
      showAlert(t('capabilities.capabilitySaveError'), 'error');
      console.error('Error saving capability:', err);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.name.trim() !== '' && formData.description.trim() !== '';
  const readOnly = mode === 'view';

  // Casos disponibles para un select: activos + el ya seleccionado (aunque esté inactivo),
  // excluyendo los elegidos en otras filas.
  const optionsFor = (casoIndex) => {
    const chosenElsewhere = new Set(formData.casos.filter((_, i) => i !== casoIndex).map((c) => c.caseStudyId));
    const currentId = formData.casos[casoIndex]?.caseStudyId;
    return catalog.filter((cs) => {
      const id = cs.id || cs.uid;
      if (chosenElsewhere.has(id)) return false;
      return cs.isActive !== false || id === currentId;
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' && t('capabilities.newCapability')}
        {mode === 'edit' && t('capabilities.editCapability')}
        {mode === 'view' && t('capabilities.showCapability')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showValidationErrors && validationErrors.length > 0 && !readOnly && (
            <Alert severity="error">
              <Typography variant="subtitle2">{t('validationErrors')}:</Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (<li key={index}>{error}</li>))}
              </ul>
            </Alert>
          )}

          <TextField
            label={`${t('name')} *`}
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            fullWidth
            disabled={readOnly}
          />

          <TextField
            label={`${t('description')} *`}
            value={formData.description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={readOnly}
            helperText={t('capabilities.descriptionHelper')}
          />

          {/* Casos de estudio vinculados + preguntas */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{t('capabilities.linkedCaseStudies')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('capabilities.linkedCaseStudiesHelper')}</Typography>
          </Box>

          {!catalogLoading && catalog.filter((cs) => cs.isActive !== false).length === 0 && !readOnly && (
            <Alert severity="info">{t('capabilities.noCaseStudiesInCatalog')}</Alert>
          )}

          {formData.casos.map((caso, casoIndex) => {
            const caseStudy = catalogById[caso.caseStudyId];
            const title = caseStudy?.title || `${t('capabilities.caso')} ${casoIndex + 1}`;
            return (
              <Accordion key={casoIndex} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>{title}</Typography>
                    <Chip size="small" variant="outlined"
                      label={`${caso.preguntas.filter((p) => (p.pregunta || '').trim()).length} ${t('capabilities.questions').toLowerCase()}`} />
                    {caseStudy?.isActive === false && (
                      <Chip size="small" label={t('caseStudies.inactive')} />
                    )}
                    {!readOnly && (
                      <IconButton size="small" color="error"
                        onClick={(e) => { e.stopPropagation(); removeCaso(casoIndex); }}>
                        <RemoveCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth disabled={readOnly || catalogLoading}>
                      <InputLabel>{t('capabilities.selectCaseStudy')}</InputLabel>
                      <Select
                        value={caso.caseStudyId || ''}
                        label={t('capabilities.selectCaseStudy')}
                        onChange={(e) => handleCasoSelect(casoIndex, e.target.value)}
                      >
                        {optionsFor(casoIndex).map((cs) => (
                          <MenuItem key={cs.id || cs.uid} value={cs.id || cs.uid}>
                            {cs.title}{!cs.organizationId ? ` · ${language === 'es' ? 'Por defecto' : 'Default'}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {caseStudy && (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', maxHeight: 180, overflowY: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('caseStudies.readingTime')}: {caseStudy.readingTime || 5} min
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{caseStudy.content}</Typography>
                      </Paper>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Typography variant="subtitle2">{t('capabilities.questions')}:</Typography>
                      {!readOnly && caseStudy && formData.name.trim() !== '' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            label={t('quantity')}
                            type="number"
                            value={questionsToGenerate}
                            onChange={(e) => handleQuestionsCountChange(e.target.value)}
                            size="small"
                            sx={{ width: '80px' }}
                            inputProps={{ min: 1, max: 10, step: 1 }}
                            helperText="1-10"
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => generateQuestionsWithGPT(casoIndex)}
                            disabled={gptLoading}
                          >
                            {gptLoading ? t('generating') : t('suggestWithAI')}
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {caso.preguntas.map((pregunta, preguntaIndex) => (
                      <Box key={preguntaIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label={`${t('question')} ${preguntaIndex + 1}`}
                          value={pregunta.pregunta}
                          onChange={(e) => handlePreguntaChange(casoIndex, preguntaIndex, e.target.value)}
                          fullWidth
                          disabled={readOnly}
                        />
                        {!readOnly && caso.preguntas.length > 1 && (
                          <IconButton size="small" onClick={() => removePregunta(casoIndex, preguntaIndex)} color="error">
                            <RemoveCircleIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}

                    {!readOnly && (
                      <Button variant="outlined" size="small" startIcon={<AddCircleIcon />} onClick={() => addPregunta(casoIndex)}>
                        {t('capabilities.addQuestion')}
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {!readOnly && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addCaso}>
              {t('capabilities.linkCaseStudy')}
            </Button>
          )}

        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{readOnly ? t('close') : t('cancel')}</Button>
        {!readOnly && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !canSubmit}>
            {loading ? t('saving') : t('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CapabilitiesModal;
